import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronRight,
  Sparkles,
  RefreshCw,
  Wand2,
} from 'lucide-react';
import { Header, BottomSheet } from '@ah/design/mobile';
import { LOGO_MARK_PNG } from '../assets';
import { enablePush } from '@ah/notifications/shared';
import {
  ONBOARDING_V2_CONTEXT_Q,
  SET_CHOSEN_FAIRY_GODMOTHER_M,
} from '@ah/people/shared';
import type { OnboardingV2Context } from '@ah/people/shared';
import { gql } from '../api';
import { useMe } from '../useAuth';

export const ONBOARDED_KEY = 'emissaries.onboarded.v1';

interface Underling {
  id: string;
  firstName: string;
  lastName: string;
  pictureUrl: string | null;
  pictureThumbUrl: string | null;
  gender: 'male' | 'female';
}

const MY_UNDERLING_Q = /* GraphQL */ `
  query MyUnderling {
    myUnderling {
      id
      firstName
      lastName
      pictureUrl
      pictureThumbUrl
      gender
    }
  }
`;

const SWAP_MY_UNDERLING_M = /* GraphQL */ `
  mutation SwapMyUnderling {
    swapMyUnderling {
      id
      firstName
      lastName
      pictureUrl
      pictureThumbUrl
      gender
    }
  }
`;

type SlideKind =
  | 'welcome'
  | 'community'
  | 'concept'
  | 'meet'
  | 'prepare'
  | 'plan'
  | 'interact'
  | 'notifications'
  | 'ready';

const CARD_VARIANTS = {
  enter: { opacity: 0, y: 24, scale: 0.98 },
  center: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -24, scale: 0.98 },
};

function shouldShowNotificationsSlide(): boolean {
  if (typeof Notification === 'undefined') return false;
  return Notification.permission === 'default';
}

export function Onboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useMe();

  const { data: ctx } = useQuery<OnboardingV2Context>({
    queryKey: ['onboardingV2Context'],
    queryFn: async () => {
      const res = await gql.request<{ onboardingV2Context: OnboardingV2Context }>(
        ONBOARDING_V2_CONTEXT_Q,
      );
      return res.onboardingV2Context;
    },
    staleTime: Infinity,
  });

  const { data: gm } = useQuery<Underling>({
    queryKey: ['myUnderling'],
    queryFn: async () => {
      const res = await gql.request<{ myUnderling: Underling }>(MY_UNDERLING_Q);
      return res.myUnderling;
    },
    staleTime: Infinity,
    // The `user.created` listener kicks off assignment + (if needed) synchronous
    // enrichment. Poll every 2s until we see a row so the Meet slide doesn't
    // hang on a slow first enrichment.
    refetchInterval: (q) => (q.state.data ? false : 2000),
  });

  const setSuperconnector = useMutation({
    mutationFn: (u: Underling) =>
      gql.request<unknown>(SET_CHOSEN_FAIRY_GODMOTHER_M, {
        input: {
          firstName: u.firstName,
          lastName: u.lastName,
          pictureUrl: u.pictureUrl ?? '',
          gender: u.gender,
        },
      }),
  });

  const swapSuperconnector = useMutation({
    mutationFn: async () => {
      const res = await gql.request<{ swapMyUnderling: Underling }>(SWAP_MY_UNDERLING_M);
      return res.swapMyUnderling;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(['myUnderling'], next);
    },
  });

  const [showNotifications] = useState<boolean>(shouldShowNotificationsSlide);
  const slides: SlideKind[] = useMemo(() => {
    const base: SlideKind[] = [
      'welcome',
      'community',
      'concept',
      'meet',
      'prepare',
      'plan',
      'interact',
    ];
    if (showNotifications) base.push('notifications');
    base.push('ready');
    return base;
  }, [showNotifications]);

  const [index, setIndex] = useState(0);
  const [enablingPush, setEnablingPush] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [prepareDone, setPrepareDone] = useState(false);
  const [notReadyOpen, setNotReadyOpen] = useState(false);

  const slide = slides[index]!;
  const firstName = me?.givenName ?? me?.name?.split(' ')[0] ?? ctx?.firstName ?? 'there';

  function next() {
    setIndex((i) => Math.min(i + 1, slides.length - 1));
  }

  async function finish() {
    if (finishing || !gm) return;
    setFinishing(true);
    try {
      await setSuperconnector.mutateAsync(gm);
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (e) {
      console.warn('[onboarding] setChosenSuperconnector failed', e);
    }
    try {
      localStorage.setItem('me.chosenSuperconnector', JSON.stringify({
        firstName: gm.firstName,
        lastName: gm.lastName,
        pictureUrl: gm.pictureUrl ?? '',
        gender: gm.gender,
      }));
      localStorage.setItem(ONBOARDED_KEY, '1');
    } catch {}
    navigate('/', { replace: true });
  }

  async function handleEnablePush() {
    if (enablingPush) return;
    setEnablingPush(true);
    const r = await enablePush();
    setEnablingPush(false);
    if (!r.ok) console.warn('[notifications] enablePush failed:', r.reason);
    next();
  }

  // The prepare slide auto-advances when its timer fires.
  const hidePrepareFooter = slide === 'prepare' && !prepareDone;

  return (
    <div className="h-full flex flex-col bg-background">
      <Header title="Welcome" backTo="/" />

      <main className="flex-1 overflow-y-auto px-5 py-4 flex flex-col">
        <Progress count={slides.length} index={index} />

        <div className="flex-1 flex items-center">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={slide}
              variants={CARD_VARIANTS}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-full"
            >
              {slide === 'welcome' && <WelcomeSlide firstName={firstName} />}
              {slide === 'community' && <CommunitySlide />}
              {slide === 'concept' && <ConceptSlide />}
              {slide === 'meet' &&
                (gm ? (
                  <MeetSlide
                    gm={gm}
                    swapping={swapSuperconnector.isPending}
                    onSwap={() => swapSuperconnector.mutate()}
                  />
                ) : (
                  <SlideLoading />
                ))}
              {slide === 'prepare' &&
                (gm ? (
                  <PrepareSlide
                    gm={gm}
                    done={prepareDone}
                    onDone={() => {
                      setPrepareDone(true);
                      setIndex((i) => Math.min(i + 1, slides.length - 1));
                    }}
                  />
                ) : (
                  <SlideLoading />
                ))}
              {slide === 'plan' &&
                (gm && ctx ? <PlanSlide gm={gm} ctx={ctx} /> : <SlideLoading />)}
              {slide === 'interact' &&
                (gm ? <InteractSlide gm={gm} /> : <SlideLoading />)}
              {slide === 'notifications' &&
                (gm ? <NotificationsSlide gm={gm} /> : <SlideLoading />)}
              {slide === 'ready' && (gm ? <ReadySlide gm={gm} /> : <SlideLoading />)}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <footer
        className="shrink-0 bg-background/95 backdrop-blur-sm border-t border-border px-4"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex flex-col gap-2 py-3 min-h-[60px]">
          {slide === 'notifications' ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={next}
                disabled={enablingPush}
                className="h-12 px-5 rounded-full text-sm font-medium text-muted-foreground disabled:opacity-60"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleEnablePush}
                disabled={enablingPush}
                className="flex-1 flex items-center justify-center gap-2 h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-[0.98] transition-all shadow-md shadow-primary/20 disabled:opacity-70"
              >
                {enablingPush ? (
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-primary-foreground/60 border-t-transparent animate-spin" />
                ) : (
                  'Allow notifications'
                )}
              </button>
            </div>
          ) : slide === 'ready' ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={finish}
                disabled={finishing || !gm}
                className="flex-1 flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-[0.98] transition-all shadow-md shadow-primary/20 disabled:opacity-70"
              >
                {finishing || !gm ? (
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-primary-foreground/60 border-t-transparent animate-spin" />
                ) : (
                  'Yes'
                )}
              </button>
              <button
                type="button"
                onClick={() => setNotReadyOpen(true)}
                disabled={finishing || !gm}
                className="flex items-center justify-center px-6 h-12 rounded-full border border-border bg-card text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/40 active:scale-[0.98] transition-all disabled:opacity-60"
              >
                No
              </button>
            </div>
          ) : !hidePrepareFooter ? (
            <button
              type="button"
              onClick={next}
              className="flex items-center justify-center gap-2 h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-[0.98] transition-all shadow-md shadow-primary/20"
            >
              Next
              <ChevronRight size={16} />
            </button>
          ) : null}
        </div>
      </footer>

      <BottomSheet
        open={notReadyOpen}
        onClose={() => setNotReadyOpen(false)}
        title={gm?.firstName ?? ''}
        leading={
          gm?.pictureThumbUrl ?? gm?.pictureUrl ? (
            <img
              src={gm.pictureThumbUrl ?? gm.pictureUrl ?? ''}
              alt={gm.firstName}
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : null
        }
      >
        <div className="px-4 pt-4 pb-5">
          <p className="text-[15px] text-foreground/85 leading-relaxed">
            No problem. Let me know when you're ready.
          </p>
        </div>
      </BottomSheet>
    </div>
  );
}

// ─── Shared visuals ─────────────────────────────────────────────────────────

function H2({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <h2 className={`text-2xl font-semibold tracking-tight text-foreground leading-snug max-w-[30ch] mx-auto ${className}`}>
      {children}
    </h2>
  );
}

function Body({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-base leading-relaxed text-foreground/80 max-w-[32ch] mx-auto ${className}`}>
      {children}
    </p>
  );
}

function SlideLoading() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function Progress({ count, index }: { count: number; index: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5 pb-6">
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className={`h-1 rounded-full transition-all ${
            i === index ? 'w-5 bg-primary' : 'w-1 bg-muted-foreground/25'
          }`}
        />
      ))}
    </div>
  );
}

function SuperconnectorAvatar({ gm, size = 'sm' }: { gm: Underling; size?: 'sm' | 'lg' }) {
  const px = size === 'lg' ? 'w-32 h-32 border-4' : 'w-20 h-20 border-2';
  const src = gm.pictureThumbUrl ?? gm.pictureUrl;
  return (
    <div className="flex justify-center mb-5">
      <div className="relative">
        {src ? (
          <img
            src={src}
            alt={gm.firstName}
            className={`${px} rounded-full object-cover border-primary/20 shadow-md shadow-primary/10`}
          />
        ) : (
          <div className={`${px} rounded-full bg-primary/10 border-primary/20 grid place-items-center text-primary font-semibold text-2xl`}>
            {gm.firstName[0]}
          </div>
        )}
        <span
          className={`absolute -bottom-1 -right-1 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground font-semibold uppercase tracking-wider shadow-md ${
            size === 'lg' ? 'text-[10px] px-2.5 py-1' : 'text-[9px] px-2 py-0.5'
          }`}
        >
          <Sparkles size={size === 'lg' ? 10 : 9} /> AI
        </span>
      </div>
    </div>
  );
}

// ─── Slides ─────────────────────────────────────────────────────────────────

function WelcomeSlide({ firstName }: { firstName: string }) {
  return (
    <div className="text-center px-2 space-y-5">
      <div className="flex justify-center">
        <motion.img
          src={LOGO_MARK_PNG}
          alt="Underlings"
          initial={{ opacity: 0, y: 8, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="w-24 h-24 object-contain"
        />
      </div>
      <H2>
        Hi {firstName} <span aria-hidden>👋</span>
      </H2>
      <H2>Welcome to Underlings.</H2>
    </div>
  );
}

function CommunitySlide() {
  return (
    <div className="text-center px-2">
      <H2>
        Underlings is a community of leaders who build things together, and help each other succeed.
      </H2>
    </div>
  );
}

function ConceptSlide() {
  return (
    <div className="text-center px-2">
      <H2>
        Every member gets a personal Underling - an AI agent who networks 24/7 on your behalf.
      </H2>
    </div>
  );
}

function MeetSlide({
  gm,
  swapping,
  onSwap,
}: {
  gm: Underling;
  swapping: boolean;
  onSwap: () => void;
}) {
  return (
    <div className="text-center px-2">
      <SuperconnectorAvatar gm={gm} size="lg" />
      <H2>
        Your Underling is <span className="text-primary">{gm.firstName}</span>.
      </H2>
      <button
        type="button"
        onClick={onSwap}
        disabled={swapping}
        className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-full border border-border disabled:opacity-60"
      >
        {swapping ? (
          <span className="inline-block h-3 w-3 rounded-full border-2 border-muted-foreground/60 border-t-transparent animate-spin" />
        ) : (
          <RefreshCw size={12} />
        )}
        Change Underling
      </button>
    </div>
  );
}

const PREPARE_DURATION_MS = 2500;

function PrepareSlide({
  gm,
  done,
  onDone,
}: {
  gm: Underling;
  done: boolean;
  onDone: () => void;
}) {
  useEffect(() => {
    if (done) return;
    const t = setTimeout(onDone, PREPARE_DURATION_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  return (
    <div className="text-center px-2">
      <SuperconnectorAvatar gm={gm} />
      <H2>Give me a second to read your profile…</H2>
      <div className="mt-7 max-w-[28ch] mx-auto">
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: PREPARE_DURATION_MS / 1000, ease: 'easeOut' }}
          />
        </div>
      </div>
    </div>
  );
}

function PlanSlide({ gm, ctx }: { gm: Underling; ctx: OnboardingV2Context }) {
  const actions = ctx.plannedActions ?? [];

  return (
    <div className="text-center px-2">
      <SuperconnectorAvatar gm={gm} />
      <H2>
        Okay, I'm all caught up. Based on your profile, I plan to:
      </H2>
      <ul className="mt-6 space-y-2.5 max-w-[36ch] mx-auto text-left">
        {actions.map((label, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 + i * 0.12, duration: 0.4, ease: 'easeOut' }}
            className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-primary/5 border border-primary/10"
          >
            <Wand2 size={16} className="text-primary shrink-0 mt-0.5" />
            <span className="text-[15px] text-foreground/85 leading-snug">{label}</span>
          </motion.li>
        ))}
      </ul>
      {actions.length > 0 && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 + actions.length * 0.12, duration: 0.4 }}
          className="mt-4 text-[13px] text-muted-foreground italic"
        >
          …and lots more.
        </motion.p>
      )}
    </div>
  );
}

function InteractSlide({ gm }: { gm: Underling }) {
  return (
    <div className="text-center px-2">
      <SuperconnectorAvatar gm={gm} />
      <H2>Interacting with me is very simple — just answer questions.</H2>
      <Body className="mt-4">
        Each answer helps me serve you better.
      </Body>
    </div>
  );
}

function NotificationsSlide({ gm }: { gm: Underling }) {
  return (
    <div className="text-center px-2">
      <SuperconnectorAvatar gm={gm} />
      <H2>
        Want me to notify you about priority invites?
      </H2>
      <Body className="mt-4">Only when it's worth your time.</Body>
    </div>
  );
}

function ReadySlide({ gm }: { gm: Underling }) {
  return (
    <div className="text-center px-2 w-full">
      <SuperconnectorAvatar gm={gm} size="lg" />
      <H2>Shall we get started?</H2>
    </div>
  );
}
